/**
 * FTR5 / FTR5b / M1.1 / M3 FLIGHT-FEEL: player ship.
 *
 * LINEAR physics (thrust / gravity hook / damping) runs on a FIXED 120 Hz step
 * via an accumulator, with render interpolation between the two latest snapshots.
 *
 * M3 FLIGHT-FEEL A1: ROTATION is now ALSO inside the fixed step — driven by a
 * rate-servo TORQUE (commandTorque) through `integrate`, so the ship has real
 * angular inertia (it spins up and coasts to a stop with felt weight) instead of
 * the old instant angular-velocity lerp. Released stick decays angVel via the
 * spec's angularDamping when rotational assist is ON.
 *
 * M1.1 BUG-1 FIX (PRESERVED): the FULL per-frame mouse delta must be consumed
 * even on high-refresh frames that run ZERO physics substeps. Because rotation
 * now lives in the fixed step, `applyLook()` first CONVERTS the whole-frame mouse
 * delta into a HELD COMMAND/RATE for the frame (`mousePitchCmd`/`mouseYawCmd`,
 * normalised by frameDt*keyTurnRate) BEFORE the substep loop runs. Every substep
 * this frame then applies that command, so a zero-substep frame still registers
 * the look on the NEXT substep — the delta is never dropped. `applyLook()` is now
 * a thin shim that only stashes the per-frame mouse command (the actual rotation
 * happens in tickLinear via torque).
 *
 * M3 FLIGHT-FEEL A2: linear assist (`assist`) and rotational assist (`rotAssist`)
 * are independent. Damping coefficients are zeroed per tick when their flag is
 * off (dampFactor(0)=1 → no-op), so each domain damps iff its own flag is on.
 */
import {
  makeRigidBody,
  integrate,
  integrateAngular,
  commandTorque,
  stickZero,
  stickUpdate,
  stickOutput,
  DEFAULT_STICK,
  rotateVec3,
  add,
  sub,
  scale,
  dot,
  length,
  vec3,
  freshState,
  tickResources,
  inBlueZone,
  balancedPips,
  addPip,
  engMultiplier,
  wepMultiplier,
  sysMultiplier,
  SHIP_HAULER,
  type RigidBody,
  type Vec3,
  type Quat,
  type ShipSpec,
  type ShipState,
  type AngularDrive,
  type PowerPips,
  type StickParams,
  type StickState,
} from '@blackmarble/sim-core';
import type { InputState } from '../input/input.js';

/** FLIGHT-FEEL A3: per-class boost/afterburner tuning, keyed by ship id. */
interface BoostSpec {
  mult: number; // thrust multiplier while boosting
  duration: number; // active seconds
  cooldown: number; // seconds before boost is ready again
  energyCost: number; // energy spent on activation
  kickKmS: number; // one-shot forward velocity kick on activation
}
const BOOST_BY_CLASS: Record<string, BoostSpec> = {
  fighter: { mult: 2.2, duration: 1.5, cooldown: 4, energyCost: 35, kickKmS: 6 },
  hauler: { mult: 1.8, duration: 1.8, cooldown: 6, energyCost: 30, kickKmS: 4 },
  heavy: { mult: 1.6, duration: 2.2, cooldown: 9, energyCost: 28, kickKmS: 3 },
};
const boostSpecFor = (id: string): BoostSpec => BOOST_BY_CLASS[id] ?? BOOST_BY_CLASS.hauler!;

/** FLIGHT-FEEL A5: blue-zone gives this much extra turn rate (mechanical payoff). */
const BLUE_ZONE_RATE_BONUS = 1.25;

/** A body the ship can collide with / orbit-hold against. */
export interface CollisionBody {
  worldPos: Vec3;
  radiusKm: number;
}

const FLOOR_FACTOR = 1.01; // ship cannot go below 1.01x body radius
const ORBIT_HOLD_FACTOR = 1.3; // drop settles at ~1.3x radius if closer than this
const SIM_DT = 1 / 120;
const DROP_SPEED = 8; // km/s retained when dropping from supercruise
// Anti-softlock: at EMPTY fuel keep a reduced "limp" thrust (no boost) so the ship
// can always crawl to a station, and passively recover fuel so you're never
// permanently stranded. Full passive refill takes ~2 min of coasting.
const LIMP_THRUST_FRAC = 0.2;
const FUEL_REGEN_FRAC_PER_SEC = 1 / 120;

export interface ShipSnapshot {
  position: Vec3;
  orientation: Quat;
}

export class ShipController {
  private body: RigidBody;
  private prev: ShipSnapshot;
  private accumulator = 0;
  // M2 P1: active ShipSpec drives handling; ShipState holds fuel/hull/shield/energy.
  private _spec: ShipSpec;
  private _state: ShipState;
  assist = true; // LINEAR flight-assist (damps velocity when thrust released)
  rotAssist = true; // FLIGHT-FEEL A2: ROTATIONAL assist (damps angVel; FA-off tumbles)
  thrustScale = 1;
  // FLIGHT-FEEL A1: per-frame mouse-derived rate command, set in applyLook BEFORE
  // the substep loop so a zero-substep high-refresh frame still registers the look.
  private mousePitchCmd = 0;
  private mouseYawCmd = 0;
  // M4 CONTROL-FEEL: persistent virtual-stick deflection (Elite relative mouse).
  // Mouse deltas accumulate here; stickOutput feeds the rate servo above.
  private stick: StickState = stickZero();
  /** Live-tunable stick params (?tune=1 panel mutates these directly). */
  stickParams: StickParams = { ...DEFAULT_STICK };
  // FLIGHT-FEEL A3: boost state machine.
  private boostT = 0; // remaining active seconds
  private boostCooldown = 0; // remaining cooldown seconds
  // FLIGHT-FEEL A4: live power-distribution pips (SYS/ENG/WEP).
  private _pips: PowerPips = balancedPips();
  // M4 CONTROL-FEEL: fixed-step accumulator for the supercruise angular servo.
  private cruiseAngAccum = 0;

  constructor(start: Vec3, startOrientation?: Quat, spec: ShipSpec = SHIP_HAULER) {
    this._spec = spec;
    this._state = freshState(spec);
    this.body = makeRigidBody({
      position: start,
      mass: spec.massKg,
      inertia: spec.inertia,
      linearDamping: spec.linearDamping,
      angularDamping: spec.angularDamping, // FLIGHT-FEEL A1: real rotational damping
      ...(startOrientation ? { orientation: startOrientation } : {}),
    });
    this.prev = { position: start, orientation: this.body.orientation };
  }

  get spec(): ShipSpec {
    return this._spec;
  }
  get state(): ShipState {
    return this._state;
  }

  /** Swap the active ship (used by the ship-select menu). Preserves pose/velocity. */
  setSpec(spec: ShipSpec): void {
    this._spec = spec;
    this._state = freshState(spec);
    this.body = {
      ...this.body,
      mass: spec.massKg,
      inertia: spec.inertia,
      linearDamping: spec.linearDamping,
      angularDamping: spec.angularDamping, // FLIGHT-FEEL A1
    };
  }

  /** W1.1 (death restore): reset hull/shield/fuel/energy to a fresh state.
   * The save deliberately does NOT store ship vitals — the record only knows
   * where the vessel last stood — so resuming from the record means full vitals. */
  resetState(): void {
    this._state = freshState(this._spec);
  }

  /** W1.1 (death restore): place the ship at a saved pose, zeroing all motion.
   * Unlike teleportLookAt this takes a saved ORIENTATION directly. */
  restorePose(pos: Vec3, orientation: Quat): void {
    this.body = {
      ...this.body,
      position: { x: pos.x, y: pos.y, z: pos.z },
      velocity: { x: 0, y: 0, z: 0 },
      angularVelocity: { x: 0, y: 0, z: 0 },
      orientation: { ...orientation },
    };
    this.prev = { position: this.body.position, orientation: this.body.orientation };
    this.resetStick();
  }

  /** P2: docked-services — restore fuel to full (immutable state replace). */
  refuelFull(): void {
    this._state = { ...this._state, fuel: this._spec.fuelMax };
  }
  /** P2: docked-services — restore hull + shields to full. */
  repairFull(): void {
    this._state = { ...this._state, hull: this._spec.hullMax, shield: this._spec.shieldMax };
  }
  /** P2: on dock capture — park the ship (zero velocity). */
  parkAtDock(): void {
    this.body = { ...this.body, velocity: vec3() };
    this.prev = { position: this.body.position, orientation: this.body.orientation };
  }

  /**
   * FIX-DOCKING 1: hold the ship rigidly attached to the orbiting station each
   * frame while DOCKED. Sets position to stationWorldPos + a fixed berth offset
   * (just outside the mesh so the hull sits at the dock arm, not inside the
   * station geometry), zeroes velocity, and syncs prev for clean render interp.
   * The berth offset is ALWAYS along the world +Y axis so the parked ship sits
   * above the dock ring regardless of station orientation.
   */
  holdAtDock(stationWorldPos: Vec3, berthOffsetKm = 6): void {
    const berthPos: Vec3 = {
      x: stationWorldPos.x,
      y: stationWorldPos.y + berthOffsetKm,
      z: stationWorldPos.z,
    };
    this.body = { ...this.body, position: berthPos, velocity: vec3() };
    this.prev = { position: berthPos, orientation: this.body.orientation };
  }

  /**
   * FIX-DOCKING 2: smooth auto-approach toward the dock berth. Blends the ship
   * position toward `stationWorldPos` and bleeds velocity to zero. Returns true
   * when the ship is close enough and slow enough that the FSM can capture to
   * DOCKED (distance <= captureKm AND speed <= captureSpeedKmS).
   *
   * Uses an exponential ease (1 - e^(-k*dt)) so the motion is dt-driven and
   * deterministic — no wall-clock or RNG. The easing is applied in world-space
   * so sim-core stays pure.
   */
  autoApproachDock(
    stationWorldPos: Vec3,
    dt: number,
    captureKm = 8,
    captureSpeedKmS = 0.5,
  ): boolean {
    const EASE_K = 3.0; // position blend rate (higher = snappier approach)
    const DAMP_K = 4.0; // velocity bleed rate
    const t = 1 - Math.exp(-EASE_K * dt);
    const td = 1 - Math.exp(-DAMP_K * dt);

    const target: Vec3 = {
      x: stationWorldPos.x,
      y: stationWorldPos.y + 6,
      z: stationWorldPos.z,
    };

    const newPos: Vec3 = {
      x: this.body.position.x + (target.x - this.body.position.x) * t,
      y: this.body.position.y + (target.y - this.body.position.y) * t,
      z: this.body.position.z + (target.z - this.body.position.z) * t,
    };
    const newVel: Vec3 = {
      x: this.body.velocity.x * (1 - td),
      y: this.body.velocity.y * (1 - td),
      z: this.body.velocity.z * (1 - td),
    };

    this.body = { ...this.body, position: newPos, velocity: newVel };
    this.prev = { position: newPos, orientation: this.body.orientation };

    const dx = newPos.x - target.x;
    const dy = newPos.y - target.y;
    const dz = newPos.z - target.z;
    const distToTarget = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const speed = Math.sqrt(newVel.x * newVel.x + newVel.y * newVel.y + newVel.z * newVel.z);
    return distToTarget <= captureKm && speed <= captureSpeedKmS;
  }

  /** P2: on undock — small push-off along the given world-forward direction. */
  pushOff(worldFwd: Vec3): void {
    this.body = { ...this.body, velocity: scale(worldFwd, 1) };
  }
  /** Debug (AAA-13): snap the ship to a world position, zeroing velocity. */
  teleport(pos: Vec3): void {
    this.body = { ...this.body, position: { x: pos.x, y: pos.y, z: pos.z }, velocity: { x: 0, y: 0, z: 0 } };
  }

  /**
   * Debug (AAA-13): snap position + zero velocity + set facing so -Z body axis
   * points from `pos` toward `lookAtPos`. Uses a pure-math look-at quaternion
   * (no THREE dependency — client-only, sim-core stays pure).
   */
  teleportLookAt(pos: Vec3, lookAtPos: Vec3): void {
    const fwd = normVec3(sub(lookAtPos, pos)); // world-space -Z target
    const q = quatLookAt(fwd);
    this.body = {
      ...this.body,
      position: { x: pos.x, y: pos.y, z: pos.z },
      velocity: { x: 0, y: 0, z: 0 },
      angularVelocity: { x: 0, y: 0, z: 0 },
      orientation: q,
    };
    this.prev = { position: this.body.position, orientation: q };
  }
  /** P4: drain energy on firing (immutable state replace, clamped >= 0). */
  spendEnergy(amount: number): void {
    this._state = { ...this._state, energy: Math.max(0, this._state.energy - amount) };
  }

  /** AAA-14: take incoming weapon damage — shield absorbs first, hull takes the
   * overflow, both clamped >= 0 (immutable state replace). */
  takeDamage(amount: number): void {
    const dmg = Math.max(0, amount);
    const shield = Math.max(0, this._state.shield - dmg);
    const overflow = Math.max(0, dmg - this._state.shield);
    const hull = Math.max(0, this._state.hull - overflow);
    this._state = { ...this._state, shield, hull };
  }

  get worldPos(): Vec3 {
    return this.body.position;
  }
  get velocity(): Vec3 {
    return this.body.velocity;
  }
  get orientation(): Quat {
    return this.body.orientation;
  }
  get speed(): number {
    const v = this.body.velocity;
    return Math.hypot(v.x, v.y, v.z);
  }

  // --- FLIGHT-FEEL A4: power pips (HUD reads these; allocation via step()). ---
  get pips(): PowerPips {
    return this._pips;
  }

  // --- FLIGHT-FEEL A3: boost state (HUD + juice read these). ---
  /** True while the afterburner is firing. */
  get boosting(): boolean {
    return this.boostT > 0;
  }
  /** True when boost is off cooldown and ready to fire. */
  get boostReady(): boolean {
    return this.boostCooldown <= 0;
  }
  /** 0..1 — boost remaining as a fraction of this class's duration (for juice). */
  get boost01(): number {
    const d = boostSpecFor(this._spec.id).duration;
    return d > 0 ? Math.max(0, Math.min(1, this.boostT / d)) : 0;
  }

  /** FLIGHT-FEEL A5: true when speed sits in the spec's optimal handling band. */
  get inBlueZone(): boolean {
    return inBlueZone(this.speed, this._spec);
  }

  /** FLIGHT-FEEL A4: ENG-pip-scaled top speed (HUD speed01 + blue-zone reference). */
  get effectiveMaxSpeedKmS(): number {
    return this._spec.maxSpeedKmS * engMultiplier(this._pips);
  }

  /**
   * M4 CONTROL-FEEL: virtual-stick mouse (Elite relative-mouse model). The
   * whole-frame mouse delta accumulates into a PERSISTENT stick deflection
   * (sim-core stickUpdate), whose shaped output becomes the held -1..1 rate
   * command (mousePitchCmd/mouseYawCmd) consumed by the fixed-step torque servo
   * in tickLinear / supercruiseStep. The ship therefore turns through its
   * physics model — angular inertia on spin-up, damping on release — instead of
   * the previous DIRECT quaternion write (which had zero inertia and read as a
   * floating camera near stations/ships).
   *
   * PRESERVES the M1.1 BUG-1 fix: deflection is state, not a consumed delta —
   * a zero-substep high-refresh frame still registers the look on the next
   * substep. NOT the pre-b7b41a3 servo mapping (delta/frameDt was framerate-
   * coupled and spiky); deflection-as-state is framerate-independent.
   */
  applyLook(dt: number, input: InputState): void {
    this.stick = stickUpdate(
      this.stick,
      -input.mouseDX,
      -input.mouseDY,
      Math.max(0, dt),
      this.stickParams,
    );
    const out = stickOutput(this.stick, this.stickParams);
    this.mousePitchCmd = out.pitch;
    this.mouseYawCmd = out.yaw;
  }

  /**
   * M4 CONTROL-FEEL: zero the stick + mouse commands. Called when the player
   * loses mouse authority (docked / menu open / pointer-lock loss / verify
   * beat). Deliberately does NOT touch angularVelocity — FA-off momentum and
   * in-flight rotation decay stay physical (the servo coasts the rate down).
   */
  resetStick(): void {
    this.stick = stickZero();
    this.mousePitchCmd = 0;
    this.mouseYawCmd = 0;
  }

  /** Shaped stick deflection for the HUD indicator, in SCREEN space: mouse
   * right (nose-right command, negative yaw cmd) → dot right (+x); mouse up
   * (nose-up command, positive pitch cmd) → dot up (+y). */
  get stickDeflection(): { x: number; y: number } {
    const out = stickOutput(this.stick, this.stickParams);
    return { x: -out.yaw, y: out.pitch };
  }

  /** Advance the LINEAR + ANGULAR fixed-step sim by `frameDt` seconds of wall time. */
  step(frameDt: number, input: InputState): void {
    if (input.toggleAssist) {
      this.assist = !this.assist;
      this.rotAssist = this.assist; // F toggles BOTH (Elite default)
    }
    if (input.toggleRotAssist) this.rotAssist = !this.rotAssist; // V toggles rot alone
    if (input.pipSys) this._pips = addPip(this._pips, 'sys');
    if (input.pipEng) this._pips = addPip(this._pips, 'eng');
    if (input.pipWep) this._pips = addPip(this._pips, 'wep');
    this.tryBoost(input);
    this.accumulator += Math.min(frameDt, 0.25); // clamp huge stalls
    while (this.accumulator >= SIM_DT) {
      this.prev = { position: this.body.position, orientation: this.body.orientation };
      this.body = this.tickLinear(this.body, input);
      this.tickBoostTimers(SIM_DT);
      // M2 P1: resource tick per fixed step (fuel burn ∝ commanded thrust magnitude,
      // shield/energy regen). Out-of-fuel coasts (no force applied below).
      const thrustFrac = Math.min(
        1,
        Math.hypot(input.strafe, input.vertical, input.throttle),
      );
      this._state = this.tickResourcesWithPips(thrustFrac);
      this.accumulator -= SIM_DT;
    }
  }

  /** FLIGHT-FEEL A3: fire boost on the input edge if ready + energised. */
  private tryBoost(input: InputState): void {
    if (!input.boost || this.boostCooldown > 0 || this.boostT > 0) return;
    const b = boostSpecFor(this._spec.id);
    if (this._state.energy < b.energyCost) return;
    this.spendEnergy(b.energyCost);
    this.boostT = b.duration;
    this.boostCooldown = b.cooldown;
    // One-shot velocity kick along body-forward (-Z), scaled by ENG pips.
    const fwd = rotateVec3(this.body.orientation, vec3(0, 0, -1));
    const kick = b.kickKmS * engMultiplier(this._pips);
    this.body = { ...this.body, velocity: add(this.body.velocity, scale(fwd, kick)) };
  }

  /** FLIGHT-FEEL A3: tick boost active + cooldown timers down each fixed step. */
  private tickBoostTimers(dt: number): void {
    if (this.boostT > 0) this.boostT = Math.max(0, this.boostT - dt);
    if (this.boostCooldown > 0) this.boostCooldown = Math.max(0, this.boostCooldown - dt);
  }

  /** FLIGHT-FEEL A1: build the -1..1 per-axis rotation command for this step. */
  private rotCommand(input: InputState): Vec3 {
    return {
      x: clamp(-input.keyPitch + this.mousePitchCmd, -1, 1),
      y: clamp(-input.keyYaw + this.mouseYawCmd, -1, 1),
      z: clamp(-input.roll, -1, 1),
    };
  }

  /** FLIGHT-FEEL A1/A5: rate-servo drive; blue-zone boosts the reachable max rate. */
  private buildDrive(): AngularDrive {
    const s = this._spec;
    const { ix, iy, iz } = this._spec.inertia;
    const rateMul = this.inBlueZone ? BLUE_ZONE_RATE_BONUS : 1;
    return {
      maxRate: vec3(s.keyTurnRate * rateMul, s.keyTurnRate * rateMul, s.rollRate * rateMul),
      accel: vec3(s.pitchYawAccel, s.pitchYawAccel, s.rollAccel),
      // Per-axis torque cap = inertia * maxAngAccel (rad/s^2 bound on the servo).
      maxTorque: vec3(ix * s.maxAngAccel, iy * s.maxAngAccel, iz * s.maxAngAccel),
    };
  }

  /**
   * FLIGHT-FEEL A4: run the PURE tickResources, then apply WEP (energy regen) and
   * SYS (shield regen) pip multipliers as a post-hoc BONUS delta — keeps the pure
   * sim-core tickResources + its tests untouched (multipliers live in the client).
   */
  private tickResourcesWithPips(thrustFrac: number): ShipState {
    const next = tickResources(this._spec, this._state, SIM_DT, thrustFrac);
    const wepBonus = (wepMultiplier(this._pips) - 1) * this._spec.energyRegenPerSec * SIM_DT;
    const sysBonus = (sysMultiplier(this._pips) - 1) * this._spec.shieldRegenPerSec * SIM_DT;
    const clamp01 = (v: number, max: number): number => Math.max(0, Math.min(max, v));
    // Anti-softlock: passive fuel trickle so empty fuel always recovers over time
    // (you deplete under thrust faster than this, but recover while coasting).
    const fuelRegen = this._spec.fuelMax * FUEL_REGEN_FRAC_PER_SEC * SIM_DT;
    return {
      ...next,
      energy: clamp01(next.energy + wepBonus, this._spec.energyMax),
      shield: clamp01(next.shield + sysBonus, this._spec.shieldMax),
      fuel: clamp01(next.fuel + fuelRegen, this._spec.fuelMax),
    };
  }

  private tickLinear(body: RigidBody, input: InputState): RigidBody {
    const local = vec3(input.strafe, input.vertical, -input.throttle);
    // M2 P1: thrust + linearDamping from the active spec. Empty fuel = coast.
    const empty = this._state.fuel <= 0;
    // FLIGHT-FEEL A3/A4: boost multiplier + ENG-pip scaling on thrust magnitude.
    const boostMul = this.boostT > 0 ? boostSpecFor(this._spec.id).mult : 1;
    // Anti-softlock: empty fuel keeps a reduced LIMP thrust (no boost) instead of 0,
    // so the player can always crawl somewhere rather than being stranded forever.
    const thrustMul = empty ? LIMP_THRUST_FRAC : boostMul;
    const fmag = this._spec.thrust * this.thrustScale * thrustMul * engMultiplier(this._pips);
    const force = scale(rotateVec3(body.orientation, local), fmag);
    // FLIGHT-FEEL A1: rate-servo torque gives real angular inertia.
    const torque = commandTorque(body, this.rotCommand(input), this.buildDrive());
    // FLIGHT-FEEL A2: zero each damping coeff when its flag is off (dampFactor(0)=1
    // → no-op), so each domain damps iff its own flag is on, despite the shared
    // `assist` bool inside integrate.
    const stepBody: RigidBody = {
      ...body,
      linearDamping: this.assist ? this._spec.linearDamping : 0,
      angularDamping: this.rotAssist ? this._spec.angularDamping : 0,
    };
    return integrate(stepBody, force, torque, SIM_DT, this.assist || this.rotAssist);
  }

  /**
   * FLIGHT-2: KINEMATIC supercruise translation + M4 SERVO rotation. Moves the
   * ship along its facing direction (-Z body forward) at `speedKmS`, setting
   * velocity directly (Elite supercruise is effectively kinematic, not
   * force-integrated) — the LOCKED speed/drop model is untouched. ROTATION now
   * runs the same fixed-step rate-servo as normal flight (stick + arrow keys +
   * roll through commandTorque -> integrateAngular), so cruise steering carries
   * real angular inertia instead of the old direct applyLook quaternion write.
   * Call instead of step() while cruising.
   */
  supercruiseStep(frameDt: number, speedKmS: number, input: InputState): void {
    const dt = Math.min(frameDt, 0.1);
    this.prev = { position: this.body.position, orientation: this.body.orientation };
    // ANGULAR: fixed-step servo (mirrors tickLinear's rotational half).
    this.cruiseAngAccum += dt;
    while (this.cruiseAngAccum >= SIM_DT) {
      const stepBody: RigidBody = {
        ...this.body,
        angularDamping: this.rotAssist ? this._spec.angularDamping : 0,
      };
      const torque = commandTorque(stepBody, this.rotCommand(input), this.buildDrive());
      this.body = integrateAngular(stepBody, torque, SIM_DT, this.rotAssist);
      this.cruiseAngAccum -= SIM_DT;
    }
    // LINEAR: kinematic along the (freshly rotated) facing.
    const fwd = rotateVec3(this.body.orientation, vec3(0, 0, -1));
    const vel = scale(fwd, speedKmS);
    this.body = { ...this.body, velocity: vel, position: add(this.body.position, scale(vel, dt)) };
    this.accumulator = 0; // keep render interpolation aligned
  }

  /** Clamp velocity to a sane low-orbit speed when dropping out of supercruise. */
  dropFromSupercruise(): void {
    const sp = this.speed;
    if (sp > DROP_SPEED) {
      const f = DROP_SPEED / sp;
      this.body = { ...this.body, velocity: scale(this.body.velocity, f) };
    }
  }

  /**
   * COLLISION-1: spherical surface floor. The ship cannot pass below
   * FLOOR_FACTOR × radius of `body`; on contact it is pushed back to the floor
   * and its INWARD (radial) velocity is arrested (slide, not bounce). Tangential
   * motion is preserved so you skim the surface. Call every frame vs nearest body.
   */
  collide(body: CollisionBody): void {
    const toShip = sub(this.body.position, body.worldPos);
    const dist = length(toShip);
    if (dist <= 0) return;
    const floor = body.radiusKm * FLOOR_FACTOR;
    if (dist >= floor) return;
    const n = scale(toShip, 1 / dist); // outward radial unit
    const pos = add(body.worldPos, scale(n, floor)); // snap to floor
    const radial = dot(this.body.velocity, n);
    // Remove only the inward radial component (keep tangential -> slide/skim).
    const vel = radial < 0 ? sub(this.body.velocity, scale(n, radial)) : this.body.velocity;
    this.body = { ...this.body, position: pos, velocity: vel };
    this.prev = { position: pos, orientation: this.body.orientation };
  }

  /**
   * COLLISION-1: orbit-hold on supercruise drop. If within ORBIT_HOLD_FACTOR ×
   * radius, arrest the radial velocity (no plunge) and settle the ship to a
   * controllable hover at ~1.3× radius with a small tangential drift, so the
   * player sightsees instead of face-planting. Player can then thrust to descend
   * (collide() keeps them above the floor).
   */
  orbitHold(body: CollisionBody): void {
    const toShip = sub(this.body.position, body.worldPos);
    const dist = length(toShip);
    if (dist <= 0) return;
    const hold = body.radiusKm * ORBIT_HOLD_FACTOR;
    const n = scale(toShip, 1 / dist);
    // Pull back out to the hold altitude if we dropped in too low.
    const pos = dist < hold ? add(body.worldPos, scale(n, hold)) : this.body.position;
    // Strip the radial component of velocity; keep (small) tangential.
    const radial = dot(this.body.velocity, n);
    const vel = sub(this.body.velocity, scale(n, radial));
    this.body = { ...this.body, position: pos, velocity: vel };
    this.prev = { position: pos, orientation: this.body.orientation };
  }

  /** Render snapshot interpolated between prev and current sim states. */
  renderState(): ShipSnapshot {
    const alpha = this.accumulator / SIM_DT;
    const p = add(scale(this.prev.position, 1 - alpha), scale(this.body.position, alpha));
    return { position: p, orientation: this.body.orientation };
  }
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Normalise a Vec3; returns world +Y if degenerate. */
const normVec3 = (v: Vec3): Vec3 => {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (len < 1e-9) return { x: 0, y: 1, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
};

/**
 * Build a normalised quaternion whose -Z body axis aligns with `fwd` (world).
 * Standard "camera look-at" construction: right = fwd × up, then gram-schmidt.
 * Ships face -Z (same as THREE cameras), so we build the rotation matrix and
 * extract the quaternion from it.
 */
const quatLookAt = (fwd: Vec3): Quat => {
  const up: Vec3 = Math.abs(fwd.y) > 0.999 ? { x: 0, y: 0, z: 1 } : { x: 0, y: 1, z: 0 };
  // AUDIT FIX (session 6): right = fwd × up (NOT up × fwd). The old order built a
  // REFLECTED basis (det = -1) — e.g. fwd = -Z gave right = -X — and the trace
  // quaternion extraction on a reflection returns a wrong rotation. Symptoms:
  // gotoCenotaph framed the monument off-center; faceHostile never landed inside
  // the 10° combat-lock cone despite "facing" the target dead-on.
  const right = normVec3({
    x: fwd.y * up.z - fwd.z * up.y,
    y: fwd.z * up.x - fwd.x * up.z,
    z: fwd.x * up.y - fwd.y * up.x,
  });
  // realUp = right × fwd completes the right-handed (right, realUp, -fwd) basis.
  const realUp = {
    x: right.y * fwd.z - right.z * fwd.y,
    y: right.z * fwd.x - right.x * fwd.z,
    z: right.x * fwd.y - right.y * fwd.x,
  };
  // Rotation matrix columns: right=+X, realUp=+Y, -fwd=+Z (row-major for trace).
  const m00 = right.x,  m01 = realUp.x,  m02 = -fwd.x;
  const m10 = right.y,  m11 = realUp.y,  m12 = -fwd.y;
  const m20 = right.z,  m21 = realUp.z,  m22 = -fwd.z;
  const trace = m00 + m11 + m22;
  let w: number, x: number, y: number, z: number;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    w = 0.25 / s; x = (m21 - m12) * s; y = (m02 - m20) * s; z = (m10 - m01) * s;
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
    w = (m21 - m12) / s; x = 0.25 * s; y = (m01 + m10) / s; z = (m02 + m20) / s;
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
    w = (m02 - m20) / s; x = (m01 + m10) / s; y = 0.25 * s; z = (m12 + m21) / s;
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
    w = (m10 - m01) / s; x = (m02 + m20) / s; y = (m12 + m21) / s; z = 0.25 * s;
  }
  const len = Math.sqrt(w * w + x * x + y * y + z * z);
  return { w: w / len, x: x / len, y: y / len, z: z / len };
};
